import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { MgaCashEntry, MgaPeriod } from '../useData';
import { MgaReportRoot } from './MgaReportRoot';
import { ReportHeader } from './ReportHeader';
import { cashEntryTypeLabel, formatReportMoney, reportNum, reportRound2 } from './reportMeta';

export type CashBookReportProps = {
  period: MgaPeriod;
  cash: MgaCashEntry[];
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
};

export const CashBookReport: React.FC<CashBookReportProps> = ({
  period,
  cash,
  companyLogoSrc = null,
  companyLogoAlt,
}) => {
  const ccy = period.currency || 'USD';

  const rows = useMemo(() => {
    const sorted = [...cash].sort((a, b) => {
      const da = a.entryDate ? String(a.entryDate).slice(0, 10) : '';
      const db = b.entryDate ? String(b.entryDate).slice(0, 10) : '';
      if (da !== db) return da.localeCompare(db);
      return a.id - b.id;
    });
    let running = 0;
    return sorted.map((e) => {
      const amt = reportNum(e.amountPeriod ?? e.amount);
      const signed = e.entryType === 'EXPENSE' ? -amt : amt;
      running = reportRound2(running + signed);
      return {
        e,
        periodAmt: amt,
        signed,
        balance: running,
      };
    });
  }, [cash]);

  const closing = rows.length ? rows[rows.length - 1].balance : 0;

  return (
    <MgaReportRoot>
      <ReportHeader
        period={period}
        title="Cash book statement"
        companyLogoSrc={companyLogoSrc}
        companyLogoAlt={companyLogoAlt}
      />

      <section className="mga-report-section">
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Description</th>
              <th>Crew</th>
              <th>CCY</th>
              <th className="mga-report-num">Amount</th>
              <th className="mga-report-num">Period amt</th>
              <th className="mga-report-num">Signed</th>
              <th className="mga-report-num">Balance</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="mga-report-muted">
                  No cash entries
                </td>
              </tr>
            ) : (
              rows.map(({ e, periodAmt, signed, balance }) => (
                <tr key={e.id}>
                  <td>{e.entryDate ? formatDateGlobal(e.entryDate) : '—'}</td>
                  <td>{cashEntryTypeLabel(e.entryType)}</td>
                  <td>{e.description}</td>
                  <td>{e.crewLine?.crewName || '—'}</td>
                  <td>{e.currency || ccy}</td>
                  <td className="mga-report-num">{formatReportMoney(e.amount)}</td>
                  <td className="mga-report-num">{formatReportMoney(periodAmt)}</td>
                  <td className="mga-report-num">{formatReportMoney(signed)}</td>
                  <td className="mga-report-num">{formatReportMoney(balance)}</td>
                  <td>{e.remarks || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                <td colSpan={8} className="mga-report-emph">
                  Closing balance ({ccy})
                </td>
                <td className="mga-report-num mga-report-emph">{formatReportMoney(closing)}</td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </section>
    </MgaReportRoot>
  );
};

export default CashBookReport;
